import { onSite } from './vendor-media.js'

/**
 * The review sites a venue is likely to paste, by the domains they live on.
 * Used to fill in a name the venue can still change, and to warn when the
 * name says one site and the address is on another. Any other https address
 * is a review place too, once the venue names it.
 */
const REVIEW_SITES: readonly { name: string; domains: readonly string[] }[] = [
  { name: 'Google', domains: ['google.com', 'g.page', 'goo.gl'] },
  { name: 'Facebook', domains: ['facebook.com', 'fb.com'] },
  { name: 'Yelp', domains: ['yelp.com'] },
  { name: 'The Knot', domains: ['theknot.com'] },
  { name: 'WeddingWire', domains: ['weddingwire.com'] },
  { name: 'Zola', domains: ['zola.com'] },
  { name: 'Here Comes The Guide', domains: ['herecomestheguide.com'] },
  { name: 'Tripadvisor', domains: ['tripadvisor.com'] },
  { name: 'PartySlate', domains: ['partyslate.com'] },
]

/**
 * The site a review address is on, by its host (never a substring of it), or
 * null when it isn't one we know.
 */
export function reviewSiteName(url: string): string | null {
  return REVIEW_SITES.find((site) => onSite(url, site.domains))?.name ?? null
}

/** The known site a place's name means ("yelp" is Yelp), or null for any other name. */
export function reviewSiteNamed(name: string): string | null {
  const wanted = name.trim().toLowerCase()
  return REVIEW_SITES.find((site) => site.name.toLowerCase() === wanted)?.name ?? null
}

/** Whether an address is one of Google's, so it has to be a write-a-review link. */
export function isGoogleAddress(url: string): boolean {
  return onSite(url, ['google.com', 'g.page', 'goo.gl'])
}

function parse(url: string): URL | null {
  try {
    return new URL(url)
  } catch {
    return null
  }
}

/**
 * A Google write-a-review link: the search review form, a g.page short link,
 * the Maps short link, or a google.com maps/review address. A search page is
 * not one, so it is never offered as a review.
 */
export function isGoogleReviewAddress(url: string): boolean {
  const parsed = parse(url)
  if (parsed === null) return false
  const host = parsed.hostname.toLowerCase()
  if (host === 'g.page' || host.endsWith('.g.page')) return true
  if (host === 'maps.app.goo.gl') return true
  if (host === 'search.google.com') return true
  if (!onSite(url, ['google.com'])) return false
  return parsed.pathname.includes('review') || parsed.pathname.includes('/maps')
}

/** Hosts that answer only on this machine or a private network: a couple can't open them. */
function isPrivateHost(host: string): boolean {
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal')) return true
  if (host.startsWith('[')) return true
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
}

/** Whether the path is `prefix` or under it, not merely starting with the same letters. */
function under(pathname: string, prefix: string): boolean {
  const path = pathname.toLowerCase()
  return path === prefix || path.startsWith(`${prefix}/`)
}

/**
 * Why a venue can't offer this address to couples, or null when it can. Each
 * is a mistake someone makes by copying the address from the wrong screen: the
 * venue's own dashboard (which a couple can't open), a search, or a sign-in.
 * Only addresses we are sure of are refused; anything else is the venue's call.
 */
export function reviewLinkProblem(url: string): string | null {
  const parsed = parse(url)
  if (parsed === null) return null
  const host = parsed.hostname.toLowerCase()
  if (isPrivateHost(host)) return 'Use an address anyone can open. This one only works on your own computer or network.'
  if (host === 'business.google.com') {
    return 'That’s your Google business dashboard, which couples can’t open. Use your write-a-review link.'
  }
  if (host === 'biz.yelp.com') return 'That’s your Yelp for Business page, which couples can’t open. Use your public Yelp page.'
  if (host === 'business.facebook.com') return 'That’s Meta Business Suite, which couples can’t open. Use your Facebook page.'
  if (onSite(url, ['facebook.com', 'fb.com'])) {
    if (under(parsed.pathname, '/login') || under(parsed.pathname, '/sharer') || under(parsed.pathname, '/sharer.php') || under(parsed.pathname, '/dialog')) {
      return 'That’s a Facebook sign-in or sharing link. Use your page’s address.'
    }
  }
  const searches: readonly [readonly string[], string][] = [
    [['yelp.com'], '/search'],
    [['facebook.com', 'fb.com'], '/search'],
    [['zola.com'], '/search'],
    [['zola.com'], '/wedding-vendors/search'],
  ]
  for (const [domains, prefix] of searches) {
    if (onSite(url, domains) && under(parsed.pathname, prefix)) {
      return 'That’s a search page, so couples would have to find you. Use your own page on the site.'
    }
  }
  return null
}

/** Query parameters that only say where a click came from. They don't change the page. */
const TRACKING = /^(utm_[a-z_]+|fbclid|gclid|gbraid|wbraid|dclid|msclkid|yclid|igshid|mc_cid|mc_eid|_hsenc|_hsmi|srsltid)$/i

/**
 * The address without its tracking parameters (`utm_source`, `fbclid` and the
 * like), which only record where the venue copied it from. Anything that can't
 * be read as an address is returned as it was, for validation to refuse.
 */
export function cleanReviewUrl(url: string): string {
  const parsed = parse(url)
  if (parsed === null) return url
  const kept = [...parsed.searchParams.entries()].filter(([name]) => !TRACKING.test(name))
  if (kept.length === [...parsed.searchParams.keys()].length) return url
  parsed.search = new URLSearchParams(kept).toString()
  return parsed.toString()
}

/** A Google Places ID, as GoHighLevel and Google's own links carry it. */
export function isGooglePlaceId(value: string): boolean {
  return /^[A-Za-z0-9_-]{16,}$/.test(value)
}

/** The Places ID a Google review or Maps address names, or null when it names none. */
export function googlePlaceIdOf(url: string): string | null {
  if (!isGoogleAddress(url)) return null
  const parsed = parse(url)
  if (parsed === null) return null
  const id = parsed.searchParams.get('placeid') ?? parsed.searchParams.get('query_place_id')
  return id !== null && isGooglePlaceId(id) ? id : null
}

/** Google's write-a-review form for a place. */
export function googleReviewUrlFor(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`
}

/**
 * The place on Google Maps, so someone can see whose listing it is before
 * offering it. Google's documented Maps link: the name is used only if the ID
 * can't be found.
 */
export function googleMapsUrlFor(name: string, placeId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=${encodeURIComponent(placeId)}`
}

/**
 * A Facebook page's reviews, from the page's address as GoHighLevel keeps it
 * (with or without `https://`). Null when it isn't a Facebook page address.
 */
export function facebookReviewsUrl(pageUrl: string): string | null {
  const trimmed = pageUrl.trim()
  if (trimmed === '') return null
  const parsed = parse(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`)
  if (parsed === null) return null
  parsed.protocol = 'https:'
  parsed.hash = ''
  if (!onSite(parsed.toString(), ['facebook.com', 'fb.com'])) return null
  if (reviewLinkProblem(parsed.toString()) !== null) return null
  const path = parsed.pathname.replace(/\/+$/, '')
  if (path.toLowerCase() === '/profile.php') {
    if (parsed.searchParams.get('id') === null) return null
    parsed.searchParams.set('sk', 'reviews')
    return cleanReviewUrl(parsed.toString())
  }
  const segments = path.split('/').filter((segment) => segment !== '')
  if (segments.length === 0) return null
  if (segments.at(-1)?.toLowerCase() !== 'reviews') parsed.pathname = `${path}/reviews`
  return cleanReviewUrl(parsed.toString())
}

/**
 * What might be wrong with a place that can still be saved: things a venue
 * would want to look at twice, never refused, because each can be right.
 * `googlePlaceIds` are the venue's own Google listings, as its GoHighLevel
 * sub-accounts have them.
 */
export function reviewLinkWarnings(link: { name: string; url: string }, context: { googlePlaceIds: readonly string[] }): string[] {
  const warnings: string[] = []
  const site = reviewSiteName(link.url)
  const named = reviewSiteNamed(link.name)
  if (site !== null && named !== null && site !== named) {
    warnings.push(`This is named ${named}, but the address is on ${site}.`)
  }
  const placeId = googlePlaceIdOf(link.url)
  if (placeId !== null && context.googlePlaceIds.length > 0 && !context.googlePlaceIds.includes(placeId)) {
    warnings.push('This isn’t the Google listing your GoHighLevel sub-account has. Check it opens your venue.')
  }
  return warnings
}
